// src/pages/budgeting/boq/TowerSiteBOQSitacForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  ChevronDown,
  ChevronRight,
  Package,
  Info,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import ItemPickerModal from '../../../components/boq/ItemPickerModal';
import towerSiteBOQService from '../../../services/towerSiteBOQService';
import budgetCategoryService from '../../../services/budgetCategoryService';

/**
 * Tower Site BOQ SITAC Form
 * 
 * For creating/editing SITAC (Site Acquisition) BOQ
 * SITAC has 3 simple categories:
 * - I. Harga Lahan (Land Cost)
 * - II. Perijinan (Permits)
 * - III. Jasa (Services)
 * 
 * Volume-only entry (no pricing calculations)
 * Pricing will be done in Tower Budgeting (RAP) phase
 */
const TowerSiteBOQSitacForm = ({ site, onBack, onSave }) => {
  // ============= STATE MANAGEMENT =============
  
  // Categories data
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  
  // BOQ data per category: { categoryId: { items: [...], remarks: '' } }
  const [boqData, setBOQData] = useState({});
  
  // UI state
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Item Picker Modal
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState(null);

  // ============= LIFECYCLE =============
  
  useEffect(() => {
    console.log('📍 TOWER SITE DATA RECEIVED:', site);
    
    const loadData = async () => {
      // Load categories
      setIsLoadingCategories(true);
      try {
        console.log('📂 Loading SITAC budget categories...');
        const allCategories = await budgetCategoryService.getAllCategories();
        console.log('✅ Received categories:', allCategories);
        
        // Filter only TOWER_SITAC categories (I, II, III)
        const sitacCategories = allCategories.filter(cat => 
          cat.projectType === 'TOWER_SITAC'
        );
        
        console.log('✅ Filtered SITAC categories:', sitacCategories);
        
        if (sitacCategories.length === 0) {
          console.warn('⚠️ No SITAC categories found!');
          toast.error('No SITAC budget categories available');
        } else {
          setCategories(sitacCategories);
          // Expand all categories by default
          setExpandedCategories(sitacCategories.map(cat => cat.id));
          console.log('✅ Loaded SITAC categories:', sitacCategories);
        }
      } catch (error) {
        console.error('❌ Error loading categories:', error);
        toast.error('Failed to load budget categories: ' + (error.message || 'Unknown error'));
      } finally {
        setIsLoadingCategories(false);
      }
      
      // Load existing BOQ
      setIsLoading(true);
      try {
        const boqs = await towerSiteBOQService.getSitacBOQsByTowerSiteId(site.id);
        
        if (boqs && boqs.length > 0) {
          // Transform to { categoryId: { items: [...], remarks: '' } }
          const transformed = {};
          boqs.forEach(boq => {
            transformed[boq.budgetCategoryId] = {
              boqId: boq.id,
              items: boq.items || [],
              remarks: boq.remarks || ''
            };
          });
          
          setBOQData(transformed);
          console.log('✅ Loaded existing SITAC BOQ:', transformed);
          toast.success('Existing SITAC BOQ loaded');
        }
      } catch (error) {
        console.error('❌ Error loading BOQ:', error);
        // Don't show error toast - BOQ might not exist yet
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.id]);

  // ============= DATA LOADING (moved below useEffect) =============

  // ============= UI HANDLERS =============
  
  /**
   * Toggle category expansion
   */
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };
  
  /**
   * Open item picker for a category
   */
  const handleAddItem = (categoryId) => {
    setCurrentCategoryId(categoryId);
    setShowItemPicker(true);
  };
  
  /**
   * Handle item selection from picker
   */
  const handleItemSelect = (item) => {
    if (!currentCategoryId) return;
    
    // Create new BOQ item (volume-only, no pricing)
    const newItem = {
      tempId: `temp-${Date.now()}`,
      itemId: item.id,
      itemNumber: item.itemNumber,
      itemCode: item.itemCode,
      itemDescription: item.itemDescription,
      itemType: item.itemType,
      unit: item.unit,
      volume: 1, // Default volume
      remarks: '',
      sequenceOrder: (boqData[currentCategoryId]?.items?.length || 0) + 1
    };
    
    // Add to BOQ data
    setBOQData(prev => ({
      ...prev,
      [currentCategoryId]: {
        ...prev[currentCategoryId],
        items: [...(prev[currentCategoryId]?.items || []), newItem]
      }
    }));
    
    setShowItemPicker(false);
    toast.success('Item added to BOQ');
  };
  
  /**
   * Handle item field change (volume, remarks)
   */
  const handleItemFieldChange = (categoryId, itemId, field, value) => {
    setBOQData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.map(item => {
        if ((item.tempId || item.id) === itemId) {
          return { ...item, [field]: value };
        }
        return item;
      });
      
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
  };
  
  /**
   * Delete item from BOQ
   */
  const handleDeleteItem = (categoryId, itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    setBOQData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.filter(item => 
        (item.tempId || item.id) !== itemId
      );
      
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
    
    toast.success('Item deleted from BOQ');
  };
  
  /**
   * Handle category remarks change
   */
  const handleCategoryRemarksChange = (categoryId, remarks) => {
    setBOQData(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        remarks
      }
    }));
  };
  
  /**
   * Save BOQ to backend
   */
  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Validate: at least one category must have items
      const categoriesWithItems = categories.filter(cat => 
        boqData[cat.id]?.items?.length > 0
      );
      
      if (categoriesWithItems.length === 0) {
        toast.error('Please add at least one item to any category');
        setIsSaving(false);
        return;
      }
      
      // Save each category that has items
      const savePromises = categoriesWithItems.map(category => {
        const categoryData = boqData[category.id];
        
        // Prepare request
        const request = {
          towerSiteId: site.id,
          budgetCategoryId: category.id,
          boqType: 'SITAC',
          remarks: categoryData.remarks || '',
          items: categoryData.items.map((item, index) => ({
            itemId: item.itemId,
            volume: parseFloat(item.volume) || 0,
            remarks: item.remarks || '',
            sequenceOrder: index + 1
          }))
        };
        
        return towerSiteBOQService.saveBOQ(request);
      });
      
      await Promise.all(savePromises);
      
      toast.success('SITAC BOQ saved successfully!');
      
      // Callback to parent
      if (onSave) {
        onSave(site.id);
      }
      
    } catch (error) {
      console.error('❌ Error saving BOQ:', error);
      toast.error(error.response?.data?.message || 'Failed to save BOQ');
    } finally {
      setIsSaving(false);
    }
  };

  // ============= RENDER HELPERS =============
  
  /**
   * Render single BOQ item row
   */
  const renderItemRow = (categoryId, item, index) => {
    return (
      <tr key={item.tempId || item.id} className="border-b border-gray-200 hover:bg-gray-50">
        {/* No */}
        <td className="px-4 py-3 text-center text-sm text-gray-700 font-medium">
          {index + 1}
        </td>
        
        {/* Item Info */}
        <td className="px-4 py-3">
          <div className="flex items-start gap-2">
            <Package className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-600">
                  {item.itemCode || item.itemNumber}
                </span>
                <span className="text-sm font-semibold text-gray-900 truncate">
                  {item.itemDescription}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Type: {item.itemType}
              </div>
            </div>
          </div>
        </td>
        
        {/* Unit */}
        <td className="px-4 py-3 text-sm text-gray-700 text-center">
          {item.unit}
        </td>
        
        {/* Volume */}
        <td className="px-4 py-3">
          <input
            type="number"
            value={item.volume}
            onChange={(e) => handleItemFieldChange(categoryId, item.tempId || item.id, 'volume', e.target.value)}
            min="0"
            step="0.01"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </td>
        
        {/* Remarks */}
        <td className="px-4 py-3">
          <input
            type="text"
            value={item.remarks || ''}
            onChange={(e) => handleItemFieldChange(categoryId, item.tempId || item.id, 'remarks', e.target.value)}
            placeholder="Optional notes..."
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </td>
        
        {/* Actions */}
        <td className="px-4 py-3 text-center">
          <button
            onClick={() => handleDeleteItem(categoryId, item.tempId || item.id)}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete Item"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </td>
      </tr>
    );
  };

  // ============= MAIN RENDER =============
  
  if (isLoadingCategories || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading SITAC BOQ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">BOQ SITAC - Site Acquisition</h1>
              <p className="text-gray-600 mt-1">{site.siteName} ({site.siteId})</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5" />
            {isSaving ? 'Saving...' : 'Save SITAC BOQ'}
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-1">SITAC BOQ - Volume Only</h3>
            <p className="text-sm text-blue-800">
              Enter volume/quantity for each item. Pricing will be calculated later in the Tower Budgeting (RAP) phase.
            </p>
            <p className="text-sm text-blue-800 mt-2">
              <strong>SITAC Categories:</strong> I - Harga Lahan, II - Perijinan, III - Jasa
            </p>
          </div>
        </div>
      </div>

      {/* Categories */}
      {categories.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
          <p className="text-yellow-800 font-medium">No SITAC categories available</p>
          <p className="text-sm text-yellow-700 mt-1">Please contact administrator to set up budget categories</p>
        </div>
      ) : (
        categories.map(category => {
          const isExpanded = expandedCategories.includes(category.id);
          const categoryItems = boqData[category.id]?.items || [];
          
          return (
            <div key={category.id} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              {/* Category Header */}
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="p-1 hover:bg-purple-500 rounded transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-white" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-white" />
                      )}
                    </button>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white">
                        {category.categoryCode} - {category.categoryName}
                      </h3>
                      <p className="text-sm text-purple-100 mt-1">
                        {categoryItems.length} item{categoryItems.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAddItem(category.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-purple-700 rounded-lg hover:bg-purple-50 transition-colors shadow-md"
                  >
                    <Plus className="w-4 h-4" />
                    Add Item
                  </button>
                </div>
              </div>

              {/* Category Content */}
              {isExpanded && (
                <div className="p-6">
                  {/* Category Remarks */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Category Remarks (Optional)
                    </label>
                    <textarea
                      value={boqData[category.id]?.remarks || ''}
                      onChange={(e) => handleCategoryRemarksChange(category.id, e.target.value)}
                      placeholder="Enter any notes for this category..."
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Items Table */}
                  {categoryItems.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 font-medium">No items added yet</p>
                      <p className="text-sm text-gray-500 mt-1">Click "Add Item" to add items to this category</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="w-full">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">No</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Item</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Unit</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Volume</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Remarks</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {categoryItems.map((item, index) => renderItemRow(category.id, item, index))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Item Picker Modal */}
      <ItemPickerModal
        isOpen={showItemPicker}
        onSelect={handleItemSelect}
        onClose={() => setShowItemPicker(false)}
      />
    </div>
  );
};

export default TowerSiteBOQSitacForm;